import InputText from "component/inputText/inputText"
import Checkbox from "component/checkbox/checkbox"
import IconsColor from "util/iconsColor"
import { DayWisePricingGroupLocal, WEEK_DAYS } from "./hotelContractTypes"

interface DayWisePricingProps {
    useDayWisePricing: boolean
    dayWiseGroups: DayWisePricingGroupLocal[]
    onUseDayWisePricingChange: (value: boolean) => void
    onGroupNameChange: (index: number, value: string) => void
    onDayToggle: (groupIndex: number, dayId: number) => void
    onSurchargeToggle: (groupIndex: number) => void
    onDeleteGroup: (index: number) => void
    onAddGroup: () => void
    onEnableDayGroupInRateTableToggle: (value: boolean) => void
    showValidationErrors: boolean
    /** Error raised because the contract has no season (shown below the related control). */
    seasonDependencyError?: string
}

const SeasonDependencyError = ({ message }: { message?: string }) =>
    message ? (
        <div className="error-message" style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px" }}>
            {message}
        </div>
    ) : null

const DayWisePricing = ({
    useDayWisePricing,
    dayWiseGroups,
    onUseDayWisePricingChange,
    onGroupNameChange,
    onDayToggle,
    onSurchargeToggle,
    onDeleteGroup,
    onAddGroup,
    onEnableDayGroupInRateTableToggle,
    showValidationErrors,
    seasonDependencyError,
}: DayWisePricingProps) => {
    // Calculate if all groups have flag_enable_day_group_in_rate_table enabled
    const allGroupsEnabled = dayWiseGroups.length > 0 && dayWiseGroups.every(group => group.flag_enable_day_group_in_rate_table)
    return (
        <div className="day-wise-pricing-section">
            <b>Does This Hotel Use Day-Wise Pricing?</b>
            <div className="checkbox-group">
                <Checkbox label="Yes" checked={useDayWisePricing} onChange={() => onUseDayWisePricingChange(true)} />
                <Checkbox label="No" checked={!useDayWisePricing} onChange={() => onUseDayWisePricingChange(false)} />
            </div>
            {!useDayWisePricing && <SeasonDependencyError message={seasonDependencyError} />}

            {useDayWisePricing && (
                <div className="pricing-groups">
                    <div className="pricing-groups-header">
                        <span className="header-item">Group Name</span>
                        <span className="header-item">Days Included</span>
                        <span className="header-item">Surcharge?</span>
                    </div>

                    {dayWiseGroups.map((group, groupIndex) => {
                        const canDelete = !group.flag_disabled && dayWiseGroups.length > 1
                        return (
                        <div key={group.day_wise_pricing_group_id} className="pricing-group-row">
                            <div className="group-name-input">
                                <InputText
                                    label=""
                                    value={group.day_wise_pricing_group_name || ""}
                                    placeholder="Enter Group Name"
                                    onChange={(e) => onGroupNameChange(groupIndex, e.target.value)}
                                    disabled={group.flag_disabled}
                                    error={
                                        showValidationErrors && group.day_wise_pricing_group_name === ""
                                            ? "Group name is required"
                                            : undefined
                                    }
                                />
                            </div>
                            <div className="days-selection">
                                {WEEK_DAYS.map((day) => (
                                    <button
                                        key={day.id}
                                        type="button"
                                        style={{
                                            cursor: group.flag_disabled ? "not-allowed" : "pointer",
                                            opacity: group.flag_disabled ? 0.7 : 1,
                                        }}
                                        className={`day-button ${group.days.includes(day.id) ? "active" : ""}`}
                                        onClick={() => onDayToggle(groupIndex, day.id)}
                                        disabled={group.flag_disabled}
                                    >
                                        {day.name}
                                    </button>
                                ))}
                                {showValidationErrors && group.days.length === 0 && (
                                    <div style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px" }}>
                                        Please select at least one day
                                    </div>
                                )}
                            </div>
                            <div className="actions-container">
                                <Checkbox
                                    checked={group.flag_surchange_applied}
                                    onChange={() => onSurchargeToggle(groupIndex)}
                                />
                            </div>
                            <div className="actions-container">
                                <button
                                    type="button"
                                    className="delete-icon-button"
                                    onClick={() => onDeleteGroup(groupIndex)}
                                    style={{ display: canDelete ? "block" : "none" }}
                                >
                                    <IconsColor
                                        iconName="deleteIcon"
                                        width="24px"
                                        height="24px"
                                        fillColor="none"
                                        strokeColor="currentColor"
                                    />
                                </button>
                            </div>
                        </div>
                        )
                    })}

                    <p>
                        <b className="add-group-button" onClick={onAddGroup}>
                            + Group
                        </b>
                    </p>
                    <Checkbox
                        label="Enable Day Group in Rate Table"
                        checked={allGroupsEnabled}
                        onChange={() => onEnableDayGroupInRateTableToggle(!allGroupsEnabled)}
                    />
                    <SeasonDependencyError message={seasonDependencyError} />
                </div>
            )}
        </div>
    )
}

export default DayWisePricing
